import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { Card } from "components/widgets/organisms/Card";
import { isPrescriptionPending, updatePatient } from "store/modules/prescriptionDetail";

export type Beneficiary = {
    name: string;
    cardNumber: string;
};

type Props = {
    beneficiaries: Beneficiary[];
    isLoadingBeneficiaries?: boolean;
};

export function Patient({ beneficiaries, isLoadingBeneficiaries = false }: Props) {
    const { data } = useAppSelector((state) => state.prescriptionDetail);
    const isPending = useAppSelector(isPrescriptionPending);

    const { protocol, cardNumber, ocrData, status, iAConfig } = data;

    const [selectedCardNumber, setCardNumber] = useState<string>(cardNumber);

    const [showOcrBeneficiary, setShowOcrBeneficiary] = useState<boolean>(false);

    const selectBeneficiary = useRef<HTMLSelectElement>(null);
    const dispatch = useAppDispatch();

    const ocrResult = ocrData?.filter(crop => crop.label === "nome_do_paciente")
    
    const hasPatientMatchError = data?.['reasonsRejectIA']?.some(reason => 
        reason && Object.values(reason).some(value => 
            typeof value === 'string' && value.includes("O nome do paciente não passou na regra de validação de igualdade")
        )
    );

    const hasDateError = data?.['reasonsRejectIA']?.some(reason => 
        reason && Object.values(reason).some(value => 
            typeof value === 'string' && value.includes("Não foi possível encontrar a data da receita no OCR!")
        )
    );

    useEffect(() => {
        setShowOcrBeneficiary(false);
        if(!!status?.status && beneficiaries?.length > 0) {
            setShowOcrBeneficiary(
                isPending && 
                ocrResult && 
                ocrResult.length > 0 && 
                !hasPatientMatchError && // Não tem erro de match =  orquestrador validou o nome e fez match
                !hasDateError  // Sem data válida = orquestrador não validou o nome
            )

            // O cardNumber já vem correto do orquestrador, apenas sincroniza o select
            setCardNumber(cardNumber);

            const index = beneficiaries?.findIndex(
                (beneficiary) => beneficiary.cardNumber === cardNumber
            );

            if (index >= 0 && selectBeneficiary.current) {
                selectBeneficiary.current.selectedIndex = index;
            }
        }
    }, [isPending, beneficiaries, cardNumber, ocrResult, hasPatientMatchError, hasDateError, status?.status]);


    const [ manuallyChangingBeneficiary, setManuallyChangingBeneficiary] = useState(false)

    useEffect(() => {
        if(manuallyChangingBeneficiary) return
        
        setCardNumber(cardNumber);

        const index = beneficiaries?.findIndex(
            (beneficiary) => beneficiary.cardNumber === cardNumber
        );

        if (index >= 0 && selectBeneficiary.current) {
            selectBeneficiary.current.selectedIndex = index;
        }
    }, [beneficiaries, cardNumber, manuallyChangingBeneficiary]);

    function handleChangeBeneficiary(event: ChangeEvent<HTMLSelectElement>) {
        const selectedIndex = event.target.value;
        const selectedBeneficiary = beneficiaries[selectedIndex];

        dispatch(updatePatient(selectedBeneficiary));
        setCardNumber(selectedBeneficiary.cardNumber);
        setManuallyChangingBeneficiary(true)
    }

    return (
        <Card>
            <h3 className="text-[2rem] font-bold text-vidalink-blue">Dados do paciente</h3>

            <div
                data-testid="patient-card"
                className="flex flex-col justify-between gap-4 xl:flex-row"
            >
                <div className="form-control flex-1">
                    <label htmlFor="protocol" className={showOcrBeneficiary ? "mb-7" : ""}>Protocolo</label>
                    <input type="text" disabled value={protocol} />
                </div>

                {/* Campo OCR: beneficiario */}
                <div className="form-control flex-1" >
                    <label htmlFor="protocol">Beneficiário</label>
                    { 
                        showOcrBeneficiary &&
                        ocrResult?.map((filteredResult, index) => (
                            <small
                                className={filteredResult?.ocr_score >= iAConfig?.thresholdBeneficiario ? "text-vidalink-green font-medium" : "text-vidalink-magenta font-medium"}
                                key={index}
                            >
                                { Math.floor(filteredResult.ocr_score * 100) }% confiável - {filteredResult?.ocr_score >= iAConfig?.thresholdBeneficiario ? "APROVADO" : "REPROVADO"}
                            </small>
                        )) 
                        
                    }
                    {!isLoadingBeneficiaries && beneficiaries?.length === 0 ? (
                        <small className="text-vidalink-magenta font-medium">
                            Nenhum beneficiário encontrado
                        </small>
                    ) : (
                        <select
                            data-testid="beneficiary-select"
                            disabled={!isPending}
                            onChange={handleChangeBeneficiary}
                            ref={selectBeneficiary}
                        >
                            {beneficiaries?.map((beneficiary, index) => (
                                <option key={beneficiary.cardNumber} value={index}>
                                    {beneficiary.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="form-control flex-1">
                    <label htmlFor="protocol" className={showOcrBeneficiary ? "mb-7" : ""}>Número do cartão</label>
                    <input id="protocol" type="text" disabled value={selectedCardNumber} />
                </div>
            </div>
        </Card>
    );
}
